import { Text, type DataTableColumn } from "@carbon6/c6-frontend-design";
import type { Label } from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import { DatasetPreview, formatDatasetLabel } from "@/components/dataset";
import { HatLabelChip } from "@/components/label";
import type { DatasetTableRow } from "@/page/dataset/DatasetTableRow";
import { formatDatasetStatus, formatTimestamp } from "./reviewFormatters";
import styles from "./review.module.css";

// 라벨 칩 컬럼은 폭이 좁다. 그래서 일부만 표시하고 나머지는 "외 N개" 로 줄인다.
const MAX_REVIEW_LABEL_CHIPS = 4;

export type DatasetReviewQueueColumnOptions = {
  // 섬네일 서명 URL 이 만료되면(onError) 큐를 디바운스해 다시 조회한다(60s). 없으면 폴백만 표시한다.
  onThumbnailError?: () => void;
};

// 리뷰 큐 테이블 컬럼. 다음 순서로 묶는다.
//   - 식별(섬네일, 이름, 라벨).
//   - 데이터 구성 지표(추가 파일, 카메라).
//   - 상태, 생성 시각.
// embodiment 확정 여부는 카드 뱃지가 표시한다. 설정 세부는 데이터셋 리뷰 탭에서 본다.
// 행 타입은 Dataset 목록과 공유한다. 두 목록은 같은 엔드포인트를 쓰고 `reviewed` 축만 다르다.
export function buildDatasetReviewQueueColumns({
  onThumbnailError,
}: DatasetReviewQueueColumnOptions = {}): readonly DataTableColumn<DatasetTableRow>[] {
  return [
    {
      key: "thumbnail_url",
      header: "",
      width: "72px",
      cellPadding: "tight",
      // 섬네일이 없으면(미생성, failed) initials 폴백을 표시한다.
      render: (row) => (
        <DatasetPreview
          label={row.display_name}
          imageUrl={row.thumbnail_url}
          mode="table"
          onImageError={onThumbnailError}
        />
      ),
    },
    {
      key: "display_name",
      header: "Dataset",
      width: "minmax(260px, 1.4fr)",
      render: (row) => (
        <div className={styles.nameCell}>
          <Text variant={hatcheryTextModes["body-bold"]} className={styles.nameText}>
            {row.display_name}
          </Text>
          <span className={styles.uuidText}>{row.uuid}</span>
        </div>
      ),
    },
    {
      key: "labels",
      header: "라벨",
      width: "minmax(180px, 1.4fr)",
      // dataset 의 (확정) 라벨. 일반 목록과 같이 이름 옆에 두는 정체성 라벨이다.
      render: (row) => <ReviewLabelChips labels={row.labels} />,
    },
    {
      key: "extra_file_count",
      header: "추가 파일",
      width: "100px",
      // schema 외(extra) 파일 수. 0 이면 그대로 0.
      render: (row) => <span>{row.extra_file_count}</span>,
    },
    {
      key: "camera_stream_count",
      header: "카메라",
      width: "100px",
      // 실측 스트림 수(데이터 기준). embodiment 지정 여부와 무관하게 항상 표시한다.
      render: (row) => <span>{row.camera_stream_count} ch</span>,
    },
    {
      key: "status",
      header: "상태",
      width: "180px",
      // 리뷰는 노출과 사용을 막지 않는다. 그래서 import 로 반영된 항목은 모두 활성이다. 운영자가
      // 따로 보관한 항목만 '보관됨'으로 표시된다.
      render: (row) => (
        <div className={styles.timeCell}>
          <span>{formatDatasetStatus(row.status)}</span>
          <span>{formatTimestamp(row.created_at)}</span>
        </div>
      ),
    },
  ];
}

// dataset 의 (확정) 라벨을 칩으로 그린다. 없으면 "—", 많으면 일부 + "외 N개".
function ReviewLabelChips({ labels }: { labels: readonly Label[] }) {
  if (labels.length === 0) {
    return <span className={styles.muted}>—</span>;
  }
  const visible = labels.slice(0, MAX_REVIEW_LABEL_CHIPS);
  const hiddenCount = labels.length - visible.length;
  return (
    <span className={styles.chipRow}>
      {visible.map((label, index) => (
        <HatLabelChip
          key={`${label.uuid}-${index}`}
          labelKey={label.key}
          value={label.value}
          color={label.color}
          description={label.description}
          size="xs"
        >
          {formatDatasetLabel(label)}
        </HatLabelChip>
      ))}
      {hiddenCount > 0 ? <span className={styles.muted}>외 {hiddenCount}개</span> : null}
    </span>
  );
}
